import { Temporal } from 'temporal-polyfill';
import { AirServerError } from './custom-error.js';

export const isNotNullish = <T>(value: T | undefined | null): value is T => value !== null && value !== undefined;

export const computeArrayDiffs = <T>(before: T[], after: T[], getKey: (item: T) => unknown = (item) => item) => {
  const beforeMap = new Map(before.map((item) => [getKey(item), item]));
  const afterMap = new Map(after.map((item) => [getKey(item), item]));

  const added = after.filter((item) => !beforeMap.has(getKey(item)));
  const removed = before.filter((item) => !afterMap.has(getKey(item)));
  const changed = after.filter((item) => beforeMap.get(getKey(item)) !== afterMap.get(getKey(item)));
  return { added, removed, changed };
};

export function booleanFromString(value: string): boolean;
export function booleanFromString(value: null): null;
export function booleanFromString(value: undefined): undefined;
export function booleanFromString(value: string | null | undefined): boolean | null | undefined;
export function booleanFromString(value: string | null | undefined): boolean | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  return value.toLowerCase() === 'true';
}

/**
 * More flexible boolean parser that handles various string formats for CSV parsing.
 * Accepts: null, empty string, TRUE, FALSE, False, True, true, false
 */
export function flexibleBooleanFromString(value: string): boolean;
export function flexibleBooleanFromString(value: null): null;
export function flexibleBooleanFromString(value: undefined): undefined;
export function flexibleBooleanFromString(value: string | null | undefined): boolean | null | undefined;
export function flexibleBooleanFromString(value: string | null | undefined): boolean | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;

  // Handle empty string as null
  if (value.trim() === '') return null;

  // Normalize the string and check for boolean values
  const normalized = value.trim().toLowerCase();

  if (normalized === 'true') return true;
  if (normalized === 'false') return false;

  // If it's not a recognized boolean value, throw an error
  throw new Error(`Expected boolean value, received: "${value}"`);
}

export function booleanToString(value: boolean): 'true' | 'false';
export function booleanToString(value: null): null;
export function booleanToString(value: boolean | null): 'true' | 'false' | null;
export function booleanToString(value: boolean | null): 'true' | 'false' | null {
  if (value === null) return null;
  return value ? 'true' : 'false';
}

export function intToString(value: number): string;
export function intToString(value: null): null;
export function intToString(value: number | null): string | null;
export function intToString(value: number | null): string | null {
  if (value === null) return null;
  return String(value);
}

export function intFromString(value: string): number;
export function intFromString(value: null): null;
export function intFromString(value: undefined): undefined;
export function intFromString(value: string | null | undefined): number | null | undefined;
export function intFromString(value: string | null | undefined): number | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Associates items by a key and a value into a Map.
 *
 * @param {Iterable<T>} items - The items to associate.
 * @param {(item: T) => Key} getKey - The function to extract the key from an item.
 * @param {(item: T) => Value} [getValue] - The function to extract the value from an item. If omitted, the item itself is used.
 * @return {Map<Key, Value | T>} A Map where keys are associated with values.
 */
export function associateBy<T, Key, Value>(
  items: Iterable<T>,
  getKey: (item: T) => Key,
  getValue: (item: T) => Value,
): Map<Key, Value>;
export function associateBy<T, Key>(items: Iterable<T>, getKey: (item: T) => Key): Map<Key, T>;
export function associateBy<T, Key, Value>(
  items: Iterable<T>,
  getKey: (item: T) => Key,
  getValue?: (item: T) => Value,
): Map<Key, Value | T> {
  const map = new Map<Key, Value | T>();
  for (const item of items) {
    const key = getKey(item);
    const value = getValue ? getValue(item) : item;
    map.set(key, value);
  }
  return map;
}

/**
 * Returns an array-filter predict that filters out duplicate items based on the provided key function.
 *
 * @param {(item: T) => string} keyFn - The function used to extract the key from an item.
 * @return {(item: T) => boolean} A function that takes an item and returns true if it is distinct, false otherwise.
 * @example
 * ```ts
 * const uniqueIds = ids.filter(distinct());
 * const uniqueUsers = users.filter(distinct((user) => user.id));
 * ```
 *
 */
export const distinct = <T>(keyFn: (item: T) => unknown = (item) => item): ((item: T) => boolean) => {
  const seen = new Set();
  return (item) => {
    const key = keyFn(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  };
};

/**
 * Partitions the items into two arrays based on the provided predicate function.
 *
 * @param {Iterable<T>} items - The items to partition.
 * @param {(item: T) => boolean} predicate - The function used to determine which array to place the item in.
 * @return {[T[], T[]]} An array containing two arrays: one with items that satisfy the predicate and one with items that do not.
 */
export const partitionBy = <T>(items: Iterable<T>, predicate: (item: T) => boolean): [T[], T[]] => {
  const trueItems: T[] = [];
  const falseItems: T[] = [];
  for (const item of items) {
    if (predicate(item)) {
      trueItems.push(item);
    } else {
      falseItems.push(item);
    }
  }
  return [trueItems, falseItems];
};

/**
 * Creates an iterator that yields chunks of the given array.
 *
 * @param {T[]} array - The array to chunk.
 * @param {number} chunkSize - The size of each chunk.
 * @returns {IterableIterator<T[]>} An iterator that yields chunks of the array.
 *
 */
export function chunk<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Streamlines the building of a map from key to bucket by wrapping around a
 * built-in `Map` and providing convenience methods.
 */
export class MultiMap<K, V> implements Map<K, V[]> {
  underlying = new Map<K, V[]>();
  [Symbol.toStringTag] = '[object MultiMap]';

  clear = this.underlying.clear.bind(this.underlying);
  delete = this.underlying.delete.bind(this.underlying);
  forEach = this.underlying.forEach.bind(this.underlying);
  get = this.underlying.get.bind(this.underlying);
  has = this.underlying.has.bind(this.underlying);
  /**
   * Associates the provided bucket with the given key. If a previous bucket is
   * already associated with the given key, that association is replaced.
   */
  set(key: K, bucket: V[]): this {
    this.underlying.set(key, bucket);
    return this;
  }
  /**
   * Accumulates the given `value` into the bucket associated with the provided
   * `key`. If a bucket does not exist yet, one is created.
   */
  add(key: K, value: V): this {
    const bucket = this.underlying.get(key) ?? [];
    bucket.push(value);
    return this.set(key, bucket);
  }
  get size(): number {
    return this.underlying.size;
  }

  entries = this.underlying.entries.bind(this.underlying);
  keys = this.underlying.keys.bind(this.underlying);
  values = this.underlying.values.bind(this.underlying);
  [Symbol.iterator] = this.underlying[Symbol.iterator].bind(this.underlying);

  /**
   * Groups items by key and value into a MultiMap.
   *
   * @param items - The items to group.
   * @param keyOf - The function to extract the key from an item.
   * @param valueOf - The function to extract the value from an item. Omitting
   * this function sets the value to the item with no transformations.
   */
  static groupBy<T, Key, Value>(
    items: Iterable<T>,
    keyOf: (item: T) => Key,
    valueOf: (item: T) => Value,
  ): MultiMap<Key, Value>;
  static groupBy<T, Key>(items: Iterable<T>, keyOf: (item: T) => Key): MultiMap<Key, T>;
  static groupBy<T, Key, Value>(
    items: Iterable<T>,
    keyOf: (item: T) => Key,
    valueOf?: (item: T) => Value,
  ): MultiMap<Key, Value | T> {
    const map = new MultiMap<Key, Value | T>();
    for (const item of items) {
      const key = keyOf(item);
      const value = valueOf ? valueOf(item) : item;
      map.add(key, value);
    }
    return map;
  }
}

/**
 * Converts all null values in an object to undefined, preserving keys and types (removes null from value types).
 */
export function nullsToUndefined<T extends Record<string, any>>(
  obj: T,
): {
  [K in keyof T]: null extends T[K] ? Exclude<T[K], null> | undefined : T[K];
} {
  const result = {} as any;
  for (const key in obj) {
    const value = obj[key];
    result[key] = value === null ? undefined : value;
  }
  return result;
}

export function isEmptyObject<T extends object>(x: T): x is Record<PropertyKey, never> {
  return Object.keys(x).length === 0;
}

export type EmptyObject = Record<PropertyKey, never>;

export interface SuccessfulResult {
  success: true;
}

/**
 * Type guard that checks whether an array is non-empty.
 *
 * This function refines the type of the input from `T[]` to `[T, ...T[]]`,
 * which allows TypeScript to safely infer that the array has at least one element.
 *
 * Useful when spreading an array into functions that require at least one item,
 * like `Math.max`, `maxInstant`, or similar variadic functions.
 *
 * ### Example
 * ```ts
 * const instants: Temporal.Instant[] = getInstants();
 * const latest = isNonEmptyArray(instants) ? maxInstant(...instants) : undefined;
 * ```
 *
 * @param x - The array to check
 * @returns `true` if the array is non-empty, `false` otherwise
 */
export function isNonEmptyArray<T>(x: T[]): x is [T, ...T[]] {
  return x.length > 0;
}

/**
 * Copies properties to a destination object from a source object. A property is
 * skipped if its value is undefined, regardless of whether or not the property
 * key exists in the source object.
 *
 * @param to The destination object to write to
 * @param from The source object to read from
 * @param propertyNames Properties to copy, must be common to both objects
 * @returns The destination object
 */
export function copyProperties<To extends object, From extends To, Key extends keyof From & keyof To>(
  to: To,
  from: Partial<From>,
  propertyNames: Key[],
): To {
  for (const key of propertyNames) {
    const value = from[key];
    if (value !== undefined) {
      to[key] = value;
    }
  }
  return to;
}

/**
 * Produces the given `replacement` list but preserves a value from `existing`
 * if that same value is found in the `keep` set.
 */
export function replaceListPreserving<T>(existing: T[], replacement: T[], keep: Set<T>): T[] {
  const preserved = existing.filter((value) => keep.has(value));
  return [...new Set([...replacement, ...preserved])];
}

/**
 * A more powerful version of Promise.allSettled that allows "smuggling" data into the results.
 * This is useful when you need to track which item caused which result.
 *
 * @param items - Array of items to process
 * @param asyncFn - Async function to apply to each item
 * @returns Promise that resolves to an array of results with the original data attached
 *
 */
export async function promiseAllSettledWithData<T, R>(
  items: T[],
  asyncFn: (item: T) => Promise<R>,
): Promise<Array<{ status: 'fulfilled'; value: R; data: T } | { status: 'rejected'; reason: unknown; data: T }>> {
  const promises = items.map(async (item) => {
    try {
      const result = await asyncFn(item);
      return { status: 'fulfilled' as const, value: result, data: item };
    } catch (error) {
      return { status: 'rejected' as const, reason: error, data: item };
    }
  });

  return Promise.all(promises);
}

/**
 * Calculates the percentage given a numerator and denominator, both of which can be null.
 *
 * If either the numerator or denominator is null, or if the denominator is zero, returns null.
 * Otherwise, returns an object containing:
 *   - raw: the raw division result (numerator / denominator)
 *   - percentage: the value as a percentage (raw * 100)
 *   - percentageRounded: the percentage rounded to two decimal places
 *
 * @param numerator - The numerator (nullable)
 * @param denominator - The denominator (nullable)
 * @returns An object with raw, percentage, and percentageRounded, or null if invalid input
 *
 * @example
 * ```ts
 * const result = calculatePercentage(2, 8);
 * // result = { raw: 0.25, percentage: 25, percentageRounded: 25.00 }
 *
 * const resultNull = calculatePercentage(null, 8);
 * // resultNull = null
 *
 * const resultZero = calculatePercentage(2, 0);
 * // resultZero = null
 * ```
 */
/**
 * Calculates the percentage given a numerator and denominator, both of which can be null.
 *
 * If either the numerator or denominator is null, or if the denominator is zero, returns null.
 * Otherwise, returns an object containing:
 *   - raw: the raw division result (numerator / denominator)
 *   - percentage: the value as a percentage (raw * 100)
 *   - percentageRounded: the percentage rounded to two decimal places
 *   - complementRaw: 1 - raw (the complement of the raw value)
 *   - complementPercentage: 100 - percentage (the complement as a percentage)
 *   - complementPercentageRounded: 100 - percentageRounded (the complement percentage rounded to two decimal places)
 *
 * @param numerator - The numerator (nullable)
 * @param denominator - The denominator (nullable)
 * @returns An object with raw, percentage, percentageRounded, and their complements, or null if invalid input
 *
 * @example
 * ```ts
 * const result = calculatePercentage(2, 8);
 * // result = {
 * //   raw: 0.25,
 * //   percentage: 25,
 * //   percentageRounded: 25.00,
 * //   complementRaw: 0.75,
 * //   complementPercentage: 75,
 * //   complementPercentageRounded: 75.00
 * // }
 *
 * const resultNull = calculatePercentage(null, 8);
 * // resultNull = null
 *
 * const resultZero = calculatePercentage(2, 0);
 * // resultZero = null
 * ```
 */
export function calculatePercentage(
  numerator: number | null,
  denominator: number | null,
): {
  raw: number;
  percentage: number;
  percentageRounded: number;
  complementRaw: number;
  complementPercentage: number;
  complementPercentageRounded: number;
} | null {
  if (numerator === null || denominator === null || denominator === 0) {
    return null;
  }
  const raw = numerator / denominator;
  const percentage = raw * 100;
  const percentageRounded = Math.round(percentage * 100) / 100;
  const complementRaw = 1 - raw;
  const complementPercentage = 100 - percentage;
  const complementPercentageRounded = Math.round(complementPercentage * 100) / 100;
  return {
    raw,
    percentage,
    percentageRounded,
    complementRaw,
    complementPercentage,
    complementPercentageRounded,
  };
}

export function isWithinN(value: number | null | undefined, max: number, min: number = 0): boolean {
  if (value === null || value === undefined || isNaN(value)) {
    return false;
  }

  return value >= min && value <= max;
}

export const getPersonFirstNameWithLastNameInitial = (person: {
  firstName: string | null;
  lastName: string | null;
}) => {
  const firstName = person.firstName ?? '';
  const lastNameInitial = !!person.lastName?.length ? person.lastName.charAt(0) : '';
  return `${firstName} ${lastNameInitial}`;
};

/**
 * Helper class for fluent API when getting values from a Map with non-null guarantees.
 * Provides methods to either throw an error or return a default value when key is not found.
 */
class MapGetResult<V> {
  constructor(
    private readonly val: V | undefined,
    private readonly key: unknown,
  ) {}

  /**
   * Returns the value if found, otherwise returns the provided default value.
   * @param defaultValue - The value to return if the key is not found in the map
   * @returns The map value or the default value
   */
  orDefault(defaultValue: V): V {
    return this.val ?? defaultValue;
  }

  /**
   * Returns the value if found, otherwise throws an AirServerError with a custom message.
   * @param errorMessage - Custom error message to use when throwing
   * @returns The map value (non-null)
   * @throws {AirServerError} with 'record_not_found' code and the custom message
   */
  orThrow(errorMessage: string): V {
    if (this.val === undefined) {
      throw new AirServerError('record_not_found', errorMessage);
    }
    return this.val;
  }

  /**
   * Returns the value if found, otherwise throws an AirServerError with a default message.
   * This is called when the MapGetResult is used directly without chaining.
   * @returns The map value (non-null)
   * @throws {AirServerError} with 'record_not_found' code and default message
   */
  value(): V {
    if (this.val === undefined) {
      throw new AirServerError('record_not_found', `Key ${this.key} not found`);
    }
    return this.val;
  }
}

/**

/**
 * Returns a function that gets a paginated slice of the given items.
 *
 * @param items - The items to paginate.
 * @returns A function that gets a paginated slice of the given items.
 */
export type GetPaginated<T> = (pageIndex: number, pageSize: number) => Promise<T[]> | T[];

/**
 * Returns a function that gets a paginated slice of the given items.
 *
 * @param items - The items to paginate.
 * @returns A function that gets a paginated slice of the given items.
 */
export const getPaginated =
  <T>(items: T[]): GetPaginated<T> =>
  (pageIndex: number, pageSize: number): T[] => {
    return items.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  };

/**
 * Generic paginator that supports both unbounded and bounded flows.
 *
 * - If `totalCount` is provided, it caps the number of fetches to that many pages.
 * - Always stops when an empty page is returned (works for unbounded APIs).
 * - Optional `maxPages` is a defensive guard (useful when totals are unreliable).
 *
 * @template T The type of item returned by the API.
 *
 * @param getItems Function that fetches a page of items given `pageIndex` and `pageSize`.
 * @param pageSize Number of items to fetch per page.
 * @param opts Optional configuration:
 *  - `totalCount`: known total number of items; caps the number of pages.
 *  - `maxPages`: defensive upper bound on number of pages (default: Infinity).
 *  - `stopOnEmpty`: whether to stop when a page comes back empty (default: true).
 *
 * @returns An async generator yielding `{ pageIndex, items }` for each page.
 *
 * @example
 * ```ts
 * // Unbounded: stops when the API returns an empty page.
 * for await (const { pageIndex, items } of paginate(promiseFn, 100)) {
 *   console.log(`Page ${pageIndex} has ${items.length} items`);
 * }
 *
 * // Bounded: use a known total count to cap the number of pages.
 * const total = await getTotalCount();
 * for await (const { pageIndex, items } of paginate(promiseFn, 100, { totalCount: total })) {
 *   console.log(`Page ${pageIndex} loaded`);
 * }
 *
 * // Defensive maxPages: stop after 10 pages even if the API keeps returning items.
 * for await (const page of paginate(promiseFn, 100, { maxPages: 10 })) {
 *   console.log(page.items);
 * }
 * ```
 */
export async function* paginate<T>(
  getItems: GetPaginated<T>,
  pageSize: number,
  opts?: {
    totalCount?: number; // omit => unbounded-by-count (empty page ends)
    maxPages?: number; // extra safety cap (defaults to Infinity)
    stopOnEmpty?: boolean; // normally true; set false only for exotic APIs
  },
): AsyncGenerator<{ pageIndex: number; items: T[] }, void, void> {
  const stopOnEmpty = opts?.stopOnEmpty ?? true;
  const safePageSize = Math.max(1, pageSize | 0);

  const pagesFromTotal = Number.isFinite(opts?.totalCount as number)
    ? Math.ceil(Math.max(0, opts!.totalCount as number) / safePageSize)
    : Number.POSITIVE_INFINITY;

  const maxPages = Math.min(pagesFromTotal, opts?.maxPages ?? Number.POSITIVE_INFINITY);

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex++) {
    const items = await getItems(pageIndex, safePageSize);

    if (stopOnEmpty && items.length === 0) {
      break;
    }

    yield { pageIndex, items };

    // Optional early-exit heuristic: if no total given and we got a short page,
    // many APIs imply we've reached the end.
    if (!Number.isFinite(pagesFromTotal) && items.length < safePageSize && stopOnEmpty) {
      break;
    }
  }
}

/**
 * Gets a value from a Map with non-null guarantees. Provides a fluent API similar to Java's Optional.
 *
 * @template K - The type of keys in the map
 * @template V - The type of values in the map
 * @param map - The Map to get the value from
 * @param key - The key to look up in the map
 * @returns A MapGetResult that can be used to get the value or handle missing keys
 *
 * @example
 * // Throws exception with default message if key not found
 * const value = nonNullGet(map, 'key').value();
 *
 * @example
 * // Returns default value if key not found
 * const value = nonNullGet(map, 'key').orDefault('default');
 *
 * @example
 * // Throws exception with custom message if key not found
 * const value = nonNullGet(map, 'key').orThrow('Custom error message');
 */
export function nonNullGet<K, V>(map: Map<K, V>, key: K): MapGetResult<V> {
  return new MapGetResult(map.get(key), key);
}

/**
 * Create a concurrency limiter.
 *
 * The returned `limit(fn)` function wraps a promise factory `fn`.
 *
 * - If fewer than `concurrency` tasks are active, `fn` runs immediately.
 * - If the limiter is “full” (`active >= concurrency`), the call waits until
 *   one of the running tasks finishes.
 * - Each call participates in a shared, stateful queue, so tasks are released
 *   in FIFO order.
 *
 * The internal queue holds resolve functions for promises created by tasks
 * that tried to start when the limiter was full. When a running task finishes
 * (`finally { next(); }`), it decrements `active` and pulls the next resolver
 * from the queue. That resolver resolves the promise a waiting task was
 * awaiting, so that task can now proceed. In other words, the queue is
 * effectively scheduling delayed tasks until capacity frees up, but not by
 * time; it’s by available concurrency slots.
 *
 * @param concurrency The maximum number of concurrent tasks allowed.
 * @returns A limiter function that wraps a promise factory and enforces the concurrency limit.
 *
 * @example
 * ```ts
 * const limit = createLimiter(5);
 *
 * // At most 5 promises run at once; the rest wait their turn.
 * const results = await Promise.all(
 *   array.map((item) => limit(() => promiseFn(item)))
 * );
 * ```
 */
export type Limiter = <U>(fn: () => Promise<U>) => Promise<U>;

export function createLimiter(concurrency: number): Limiter {
  let active = 0;
  const queue: Array<() => void> = [];

  const next = () => {
    active--;
    // Wake up the next waiting task (if any)
    queue.shift()?.();
  };

  return async function limit<U>(fn: () => Promise<U>): Promise<U> {
    // If at capacity, wait until someone in the queue calls us
    if (active >= concurrency) {
      await new Promise<void>((resolve) => queue.push(resolve));
    }

    active++;

    try {
      return await fn();
    } finally {
      next();
    }
  };
}

/**
 * Run a promise-returning function, ensuring the total cycle
 * lasts at least the specified duration.
 *
 * - Measures the elapsed time with {@link Temporal.Instant}.
 * - If the function finishes before `duration` has passed,
 *   waits the remaining time before resolving.
 * - If the function takes longer than `duration`, returns
 *   immediately after completion.
 *
 * @template T The return type of the promise.
 *
 * @param fn The async function to run.
 * @param duration Either:
 *  - a {@link Temporal.Duration} object (e.g. `Temporal.Duration.from({ seconds: 5 })`), or
 *  - a number of milliseconds (e.g. `5000`).
 *
 * @returns A promise resolving to the function’s return value,
 *          after ensuring the minimum duration has passed.
 *
 * @example
 * ```ts
 * // Using milliseconds
 * const result = await withDuration(async () => {
 *   await promiseFn();
 *   return 42;
 * }, 5000); // always lasts at least 5 seconds
 *
 * // Using Temporal.Duration
 * const result = await withDuration(async () => {
 *   await promiseFn();
 *   return 42;
 * }, Temporal.Duration.from({ seconds: 5 }));
 * ```
 */
export async function withDuration<T>(fn: () => Promise<T>, duration: Temporal.Duration | number): Promise<T> {
  const start = Temporal.Now.instant();
  try {
    return await fn();
  } finally {
    const durationMs = typeof duration === 'number' ? duration : duration.total({ unit: 'millisecond' });
    if (durationMs > 0 && Number.isFinite(durationMs)) {
      const end = Temporal.Now.instant();
      const elapsed = end.epochMilliseconds - start.epochMilliseconds;
      const remaining = durationMs - elapsed;
      if (remaining > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, remaining));
      }
    }
  }
}

/**
 * Accumulate results by mapping over items from a stream of pages.
 *
 * Internally:
 * - Flattens pages into a flat stream of items.
 * - Applies `mapItem` to each item, optionally wrapped in a concurrency limiter.
 * - Collects all results and errors, while tracking basic counts.
 *
 * @template I The input item type.
 * @template R The mapped result type.
 *
 * @param opts Configuration options.
 * @param opts.pages Async stream of pages, each with an array of items.
 * @param opts.mapItem Function that maps an item to a result. Receives only the global item index.
 * @param opts.limit Optional concurrency limiter created via `createLimiter`.
 * @param opts.skippedPredicate Optional predicate to decide if a mapped value should be treated as skipped.
 * @param opts.promiseDuration Optional per-item fixed duration. Each item cycle will last at least this long. Accepts either a {@link Temporal.Duration} or a number of milliseconds.
 *
 * @returns A promise resolving to {@link AccumulateMappedResults}.
 *
 * @example
 * ```ts
 * const pages = paginate(promiseFn, 100);
 *
 * const summary = await accumulateMappedResults({
 *   pages,
 *   mapItem: async (item, { index }) => {
 *     return transformItem(item, index);
 *   },
 *   limit: createLimiter(5), // run at most 5 tasks at once
 * });
 * ```
 */
export type Page<I> = { pageIndex: number; items: I[] };

export async function* itemsFromPages<I>(pages: AsyncIterable<Page<I>>): AsyncGenerator<I> {
  for await (const { items } of pages) {
    for (const item of items) {
      yield item;
    }
  }
}

export type AccumulateMappedResults<I, R> = {
  total: number;
  results: Array<{ index: number; item: I; value: R }>;
  skipped: Array<{ index: number; item: I }>;
  errors: Array<{ index: number; item: I; error: unknown }>;
};

export async function accumulateMappedResults<I, R>(opts: {
  /** Async stream of pages; each page has an array of items. */
  pages: AsyncIterable<Page<I>>;
  /** Map a single item to a result. Receives only the global item index. */
  mapItem: (item: I, meta: { index: number }) => Promise<R> | R;
  /** Optional global concurrency limiter supplied by the caller. */
  limit?: Limiter;
  /** Optional predicate to determine if an item should be skipped. */
  skippedPredicate?: (result: R) => boolean;
  /** Optional per-item fixed duration (ms). Each item cycle lasts at least this long. */
  promiseDuration?: Temporal.Duration | number;
}): Promise<AccumulateMappedResults<I, R>> {
  const { pages, mapItem, limit, skippedPredicate, promiseDuration } = opts;

  // Flatten pages to items:
  const items = itemsFromPages(pages);

  let index = 0;
  const results: AccumulateMappedResults<I, R>['results'] = [];
  const skipped: AccumulateMappedResults<I, R>['skipped'] = [];
  const errors: AccumulateMappedResults<I, R>['errors'] = [];

  for await (const item of items) {
    try {
      const run = () => Promise.resolve(mapItem(item, { index }));
      const task = () => (limit ? limit(run) : run());
      const value = promiseDuration ? await withDuration(task, promiseDuration) : await task();
      if (skippedPredicate?.(value)) {
        skipped.push({ index, item });
      } else {
        results.push({ index, item, value });
      }
    } catch (error) {
      errors.push({ index, item, error });
    }
    index++;
  }

  return {
    total: index,
    results,
    skipped,
    errors,
  };
}
